import { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, layoutGap } from '../../../theme/theme';
import type { ValidationIssue } from '../types';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import { validationCopy } from '../copy/validationCopy';

interface ValidationRawIssueListProps {
  issues: ValidationIssue[];
}

function buildIssueKey(issue: ValidationIssue, index: number): string {
  return `${issue.ValidationId}:${issue.Message}:${issue.ElementIds.join(',')}:${index}`;
}

function buildIssueReference(issue: ValidationIssue): string | null {
  const componentName = (issue.AdditionalData?.componentName || '').trim();
  const systemName = (issue.AdditionalData?.systemName || '').trim();

  if (!componentName) {
    return null;
  }

  if (
    !systemName ||
    systemName.toLowerCase() === 'all systems' ||
    systemName.toLowerCase() === '(unassigned system)'
  ) {
    return componentName;
  }

  return `${componentName} · ${systemName}`;
}

export default function ValidationRawIssueList({ issues }: ValidationRawIssueListProps) {
  const theme = useTheme();
  const [sectionOpen, setSectionOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const copy = validationCopy.detailPanel.rawIssueDetails;

  if (issues.length === 0) {
    return null;
  }

  if (!sectionOpen) {
    return (
      <Box>
        <ActionHelpTooltip help={validationActionTooltips.rawIssueDetails}>
          <Button
            size="small"
            onClick={() => setSectionOpen(true)}
            sx={{ px: 0 }}
          >
            {copy.viewRaw}
          </Button>
        </ActionHelpTooltip>
      </Box>
    );
  }

  const visibleIssues = expanded ? issues : issues.slice(0, 5);

  return (
    <Box>
      <Stack direction="row" spacing={layoutGap.row} alignItems="center" justifyContent="space-between">
        <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
          {copy.rawIssues}
        </Typography>
        <Button
          size="small"
          onClick={() => {
            setSectionOpen(false);
            setExpanded(false);
          }}
          sx={{ px: 0, minWidth: 'auto' }}
        >
          {copy.hide}
        </Button>
      </Stack>
      <Stack spacing={layoutGap.steady} sx={{ mt: 0.7 }}>
        {visibleIssues.map((issue, index) => {
          const issueReference = buildIssueReference(issue);

          return (
            <Box
              key={buildIssueKey(issue, index)}
              sx={{
                px: 1,
                py: 0.8,
                borderRadius: `${borderRadius.sm}px`,
                border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                backgroundColor: alpha(theme.palette.background.default, 0.26),
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                {issue.Message}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {issue.Recommendation}
              </Typography>
              {!issue.AffectedElements.length && !issue.ElementIds.length && issueReference && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                  Reference: {issueReference}
                </Typography>
              )}
              {issue.AffectedElements.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                  Affected: {issue.AffectedElements.join(', ')}
                </Typography>
              )}
              {issue.ElementIds.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                  Element IDs: {issue.ElementIds.join(', ')}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>

      {issues.length > 5 && (
        <ActionHelpTooltip help={validationActionTooltips.rawIssueExpand}>
          <Button
            size="small"
            onClick={() => setExpanded((current) => !current)}
            sx={{ mt: 0.55, px: 0 }}
          >
            {expanded ? copy.showFewer : copy.viewAll(issues.length)}
          </Button>
        </ActionHelpTooltip>
      )}
    </Box>
  );
}
